import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React from "react";
import { reduxForm, FieldArray } from "redux-form";
import {
  InputField,
  RadioGroupField,
  NumericInputField,
  wrapDialog,
  AdvancedOptions,
  generateField,
  reverseFeatureIcon,
  bluntFeatureIcon,
  featureIcon,
  tgFormValues
} from "@teselagen/ui";
import { compose } from "redux";
import {
  Button,
  Intent,
  Classes,
  EditableText,
  FormGroup,
  Label,
  Callout
} from "@blueprintjs/core";
import {
  convertRangeTo0Based,
  isRangeWithinRange,
  checkIfPotentiallyCircularRangesOverlap,
  getRangeLength
} from "@teselagen/range-utils";
import {
  tidyUpAnnotation,
  getFeatureToColorMap
} from "@teselagen/sequence-utils";
import classNames from "classnames";
import { store, view } from "@risingstack/react-easy-state";
import withEditorProps from "../../withEditorProps";
import { withProps } from "recompose";
import { map, flatMap, round } from "lodash-es";
import "./style.css";
class AddOrEditAnnotationDialog extends React.Component {
  nameInputContainerRef = React.createRef();
  componentDidMount() {
    const initialNotes = this.props.initialValues && this.props.initialValues.notes || {};
    this.notes = store(
      flatMap(initialNotes, (noteValues, noteType) => {
        return map(noteValues, (value) => ({
          key: noteType,
          value
        }));
      })
    );
    this._focusTimeout = setTimeout(() => {
      const input = this.nameInputContainerRef.current?.querySelector(
        'input[name="name"]'
      );
      if (input) input.focus();
    }, 100);
  }
  componentWillUnmount() {
    clearTimeout(this._focusTimeout);
  }
  formatStart = (val) => {
    const { isProtein } = this.props.sequenceData || {};
    if (isProtein) {
      return round((val + 2) / 3);
    }
    return round(val);
  };
  formatEnd = (val) => {
    const { isProtein } = this.props.sequenceData || {};
    if (isProtein) {
      return round(val / 3);
    }
    return round(val);
  };
  parseStart = (val) => {
    const { isProtein } = this.props.sequenceData || {};
    if (isProtein) {
      return round(val * 3 - 2);
    }
    return round(val);
  };
  parseEnd = (val) => {
    const { isProtein } = this.props.sequenceData || {};
    if (isProtein) {
      return round(val * 3);
    }
    return round(val);
  };
  renderLocations = (props) => {
    const { fields } = props;
    const { sequenceData = { sequence: "" }, start, end } = this.props;
    const sequenceLength = sequenceData.sequence.length;
    const locations = fields.getAll() || [];
    return /* @__PURE__ */ jsxs("div", { children: [
      locations.length > 1 && /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx(Label, { children: "Joined Feature Spans" }),
        /* @__PURE__ */ jsxs("div", { style: { marginLeft: 50 }, children: [
          !locations.length && /* @__PURE__ */ jsxs("div", { style: { marginBottom: 10 }, children: [
            'No sub-locations. Click "Add Location" to add sub-locations for this feature.',
            " "
          ] }),
          fields.map((member, index) => {
            return /* @__PURE__ */ jsx("div", { style: {}, children: /* @__PURE__ */ jsxs("div", { style: { display: "flex", marginBottom: 10 }, children: [
              /* @__PURE__ */ jsx(
                NumericInputField,
                {
                  disabled: this.props.readOnly,
                  containerStyle: { marginBottom: 0, marginRight: 10 },
                  inlineLabel: true,
                  className: "no-inline-label-margins",
                  tooltipError: true,
                  min: 1,
                  format: this.formatStart,
                  parse: this.parseStart,
                  max: sequenceLength || 1,
                  name: `${member}.start`,
                  label: "Start"
                }
              ),
              /* @__PURE__ */ jsx(
                NumericInputField,
                {
                  disabled: this.props.readOnly,
                  containerStyle: { marginBottom: 0, marginRight: 10 },
                  inlineLabel: true,
                  className: "no-inline-label-margins",
                  tooltipError: true,
                  min: 1,
                  format: this.formatEnd,
                  parse: this.parseEnd,
                  max: sequenceLength || 1,
                  name: `${member}.end`,
                  label: "End"
                }
              ),
              /* @__PURE__ */ jsx(
                Button,
                {
                  disabled: this.props.readOnly,
                  onClick: () => {
                    if (locations.length === 2) {
                      fields.remove(0);
                      fields.remove(1);
                    } else {
                      fields.remove(index);
                    }
                  },
                  minimal: true,
                  icon: "trash"
                }
              )
            ] }) }, index);
          })
        ] })
      ] }),
      /* @__PURE__ */ jsx(
        Button,
        {
          disabled: this.props.readOnly,
          style: { marginBottom: 10, left: "50%" },
          onClick: () => {
            if (locations && locations.length) {
              fields.push({
                start: locations[locations.length - 1].end + 1,
                end: locations[locations.length - 1].end + (sequenceData.isProtein ? 3 : 2)
              });
            } else {
              const end1 = Math.max(start, end - 10);
              fields.push({
                start,
                end: end1
              });
              fields.push({
                start: end1 + 3,
                end: end1 + 10
              });
            }
          },
          icon: "add",
          children: "Add Joined Feature Span"
        }
      )
    ] });
  };
  render() {
    const {
      defaultLinkedOligoMessage,
      hideModal,
      sequenceData = { sequence: "" },
      handleSubmit,
      beforeAnnotationCreate,
      RenderTypes,
      renderTags,
      RenderBases,
      allowMultipleFeatureDirections,
      getLinkedOligoLink,
      allowPrimerBasesToBeEdited,
      bases,
      initialValues,
      forward,
      primerBindsOn,
      useLinkedOligo,
      submitting,
      change,
      annotationTypePlural,
      annotationVisibilityShow,
      renderLocations,
      locations,
      overlapsSelf,
      start,
      end,
      type,
      readOnly,
      getAdditionalEditAnnotationComps,
      advancedOptions,
      advancedDefaultOpen,
      upsertAnnotation,
      original_selectionLayerUpdate
    } = this.props;
    const { isProtein } = sequenceData;
    const sequenceLength = sequenceData.sequence.length;
    const annotationLength = getRangeLength(
      locations && locations.length ? {
        start: locations[0].start,
        end: locations[locations.length - 1].end
      } : { start, end },
      sequenceLength
    );
    const submit = handleSubmit(async (data) => {
      let updatedData;
      const forward2 = data.forward !== void 0 ? data.forward : data.arrowheadType !== "BOTTOM";
      if (annotationTypePlural === "features" && allowMultipleFeatureDirections) {
        updatedData = {
          ...data,
          strand: data.arrowheadType === "BOTTOM" ? -1 : 1
        };
        delete updatedData.forward;
      } else {
        if (forward2 === true && data.strand !== 1) {
          updatedData = { ...data, strand: 1 };
        } else if (forward2 === false && data.strand !== -1) {
          updatedData = { ...data, strand: -1 };
        } else {
          updatedData = data;
        }
      }
      const _bases = updatedData.bases || bases;
      if (!data.useLinkedOligo) {
        delete updatedData.bases;
      }
      updatedData.notes = {};
      this.notes.forEach(({ key, value }) => {
        if (!updatedData.notes[key]) updatedData.notes[key] = [];
        updatedData.notes[key].push(value || "");
      });
      if (annotationTypePlural === "features") {
        updatedData.color = getFeatureToColorMap[updatedData.type];
      }
      const hasJoinedLocations = updatedData.locations && updatedData.locations.length > 1;
      const newAnnotation = tidyUpAnnotation(
        convertRangeTo0Based({
          overlapsSelf: data.overlapsSelf,
          ...updatedData,
          ...annotationTypePlural === "primers" ? { type: "primer_bind" } : {},
          locations: void 0,
          //by default clear locations
          ...hasJoinedLocations && {
            //only add locations if there are locations
            start: updatedData.locations[0].start,
            //override the start and end to use the start and end of the joined locations
            end: updatedData.locations[updatedData.locations.length - 1].end,
            locations: updatedData.locations.map(convertRangeTo0Based)
          }
        }),
        {
          sequenceData,
          annotationType: annotationTypePlural
        }
      );
      if (beforeAnnotationCreate) {
        const shouldContinue = await beforeAnnotationCreate({
          bases: _bases,
          annotationTypePlural,
          annotation: newAnnotation,
          props: this.props,
          isEdit: !!this.props.initialValues.id
        });
        if (shouldContinue === false) return;
      }
      original_selectionLayerUpdate && original_selectionLayerUpdate({
        overlapsSelf: data.overlapsSelf,
        start: newAnnotation.start,
        end: newAnnotation.end
      });
      upsertAnnotation(newAnnotation);
      annotationVisibilityShow(annotationTypePlural);
      hideModal();
    });
    return /* @__PURE__ */ jsxs(
      "form",
      {
        onSubmit: submit,
        onKeyDown: (e) => {
          if (e.keyCode === 13 && (e.ctrlKey || e.metaKey)) {
            submit();
          }
        },
        className: classNames(
          Classes.DIALOG_BODY,
          "tg-min-width-dialog",
          "tg-upsert-annotation"
        ),
        children: [
          typeof readOnly === "string" ? /* @__PURE__ */ jsx(Callout, { intent: "primary", style: { marginBottom: 10 }, children: readOnly }) : null,
          /* @__PURE__ */ jsx("div", { ref: this.nameInputContainerRef, children: /* @__PURE__ */ jsx(
            InputField,
            {
              disabled: readOnly,
              inlineLabel: true,
              tooltipError: true,
              placeholder: "Untitled Annotation",
              ...window.__getDefaultValGenerator && window.__getDefaultValGenerator({
                code: annotationTypePlural + "_name",
                customParams: {
                  isProtein,
                  sequenceName: sequenceData.name,
                  start,
                  end
                }
              }),
              validate: required,
              name: "name",
              label: "Name"
            }
          ) }),
          !isProtein && (annotationTypePlural === "features" && allowMultipleFeatureDirections ? /* @__PURE__ */ jsx(
            StrandField,
            {
              name: "arrowheadType",
              label: "Strand",
              disabled: this.props.readOnly,
              inlineLabel: true,
              inline: true,
              tooltipError: true
            }
          ) : /* @__PURE__ */ jsx(
            RadioGroupField,
            {
              disabled: this.props.readOnly,
              inlineLabel: true,
              inline: true,
              tooltipError: true,
              options: [
                { label: "Positive", value: true },
                { label: "Negative", value: false }
              ],
              name: "forward",
              label: "Strand"
            }
          )),
          RenderTypes && /* @__PURE__ */ jsx(RenderTypes, { readOnly: this.props.readOnly, type }),
          renderTags || null,
          !renderLocations || !locations || locations.length < 2 ? /* @__PURE__ */ jsxs(React.Fragment, { children: [
            /* @__PURE__ */ jsxs(
              "div",
              {
                style: { marginBottom: 10, fontSize: 12, fontStyle: "italic" },
                children: [
                  "You can also click or drag in the editor to change the selection",
                  " "
                ]
              }
            ),
            /* @__PURE__ */ jsx(
              NumericInputField,
              {
                inlineLabel: true,
                disabled: this.props.readOnly,
                format: this.formatStart,
                parse: this.parseStart,
                tooltipError: true,
                defaultValue: start ?? 1,
                min: 1,
                max: sequenceLength || 1,
                name: "start",
                label: `${annotationTypePlural === "primers" ? "Bind " : ""}Start`
              }
            ),
            /* @__PURE__ */ jsx(
              NumericInputField,
              {
                disabled: this.props.readOnly,
                format: this.formatEnd,
                parse: this.parseEnd,
                inlineLabel: true,
                tooltipError: true,
                defaultValue: end ?? (sequenceData.isProtein ? 3 : 1),
                min: 1,
                max: sequenceLength || 1,
                name: "end",
                label: `${annotationTypePlural === "primers" ? "Bind " : ""}End`
              }
            )
          ] }) : null,
          renderLocations ? /* @__PURE__ */ jsx(FieldArray, { component: this.renderLocations, name: "locations" }) : null,
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: "bp3-text-muted bp3-text-small",
              style: { marginBottom: 15, marginTop: -5, fontStyle: "italic" },
              children: [
                `${annotationTypePlural === "primers" ? "Binding Site " : ""}Length: `,
                overlapsSelf ? sequenceLength + annotationLength : annotationLength
              ]
            }
          ),
          allowPrimerBasesToBeEdited && RenderBases ? /* @__PURE__ */ jsx(
            RenderBases,
            {
              ...{
                // ...this.props,
                bases,
                defaultLinkedOligoMessage,
                getLinkedOligoLink,
                readOnly: this.props.readOnly,
                sequenceData,
                start,
                end,
                initialValues,
                sequenceLength,
                primerBindsOn,
                forward,
                linkedOligo: initialValues.linkedOligo,
                useLinkedOligo,
                change
              }
            }
          ) : null,
          getAdditionalEditAnnotationComps && getAdditionalEditAnnotationComps(this.props),
          /* @__PURE__ */ jsx(Notes, { readOnly: this.props.readOnly, notes: this.notes }),
          /* @__PURE__ */ jsx(AdvancedOptions, { isOpenByDefault: advancedDefaultOpen, children: advancedOptions }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: { display: "flex", justifyContent: "flex-end" },
              className: "width100",
              children: [
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    style: { marginRight: 15 },
                    onMouseDown: (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      hideModal();
                    },
                    children: "Cancel"
                  }
                ),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    type: "submit",
                    loading: submitting,
                    disabled: this.props.readOnly,
                    intent: Intent.PRIMARY,
                    children: "Save"
                  }
                )
              ]
            }
          )
        ]
      }
    );
  }
}
function required(val) {
  if (!val) return "Required";
}
export default ({ formName, getProps, dialogProps }) => {
  return compose(
    wrapDialog({
      isDraggable: true,
      width: 400,
      ...dialogProps
    }),
    withEditorProps,
    withProps(getProps),
    reduxForm({
      form: formName,
      // "AddOrEditAnnotationDialog",
      validate: (values, { sequenceLength, sequenceData }) => {
        const errors = {};
        const { circular } = sequenceData || {};
        if (!circular && values.start > values.end) {
          errors.start = "Start must be less than End for a linear sequence";
          errors.end = "Start must be less than End for a linear sequence";
        }
        if (!isRangeWithinRange(
          convertRangeTo0Based(values, sequenceLength),
          { start: 0, end: sequenceLength - 1 },
          sequenceLength
        )) {
          errors.start = "Range must fit within sequence";
          errors.end = "Range must fit within sequence";
        }
        if (values.locations && values.locations.length > 1) {
          const entireLocationSpan = {
            start: values.locations[0].start,
            end: values.locations[values.locations.length - 1].end
          };
          if (entireLocationSpan.start > entireLocationSpan.end && !circular) {
            errors.locations = errors.locations || {};
            errors.locations[0] = {
              start: "In a non-circular sequence, joined spans must be in ascending order"
            };
            errors.locations[values.locations.length - 1] = {
              end: "In a non-circular sequence, joined spans must be in ascending order"
            };
          }
          values.locations.forEach((loc, index) => {
            if (index !== 0 && index !== values.locations.length - 1) {
              if (!isRangeWithinRange(loc, entireLocationSpan, sequenceLength)) {
                errors.locations = errors.locations || {};
                errors.locations[index] = {
                  start: "Joined spans must be in ascending order",
                  end: "Joined spans must be in ascending order"
                };
              }
            }
            values.locations.forEach((loc2, index2) => {
              if (loc2 === loc) return;
              if (checkIfPotentiallyCircularRangesOverlap(loc, loc2)) {
                errors.locations = errors.locations || {};
                errors.locations[index] = {
                  start: "Joined spans must not overlap",
                  end: "Joined spans must not overlap"
                };
                errors.locations[index2] = {
                  start: "Joined spans must not overlap",
                  end: "Joined spans must not overlap"
                };
              }
            });
          });
        }
        return errors;
      }
    }),
    tgFormValues(
      "start",
      "end",
      "type",
      "overlapsSelf",
      "locations",
      "bases",
      "useLinkedOligo",
      "forward",
      "primerBindsOn"
    )
  )(AddOrEditAnnotationDialog);
};
const Notes = view(({ readOnly, notes }) => {
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      FormGroup,
      {
        style: { marginBottom: 0 },
        inline: true,
        label: "Notes",
        labelInfo: "(Key -- Value)"
      }
    ),
    map(notes, (note, i) => {
      const { value, key } = note || {};
      return /* @__PURE__ */ jsxs(
        "div",
        {
          "data-test": "note-" + i,
          style: { display: "flex", padding: "4px 2px" },
          children: [
            /* @__PURE__ */ jsx(
              EditableText,
              {
                onConfirm: (string) => {
                  if (string === "") {
                    if (!note.value) {
                      notes.splice(i, 1);
                    } else {
                      note.key = "note";
                    }
                  }
                },
                disabled: readOnly,
                placeholder: "Add key here",
                maxLines: 5,
                multiline: true,
                className: "addAnnNoteKey",
                onChange: (string) => {
                  note.key = string.replace(" ", "_").replace(/\n/g, "");
                },
                value: key
              }
            ),
            /* @__PURE__ */ jsx(
              EditableText,
              {
                disabled: readOnly,
                maxLines: 10,
                className: "addAnnNoteValue",
                multiline: true,
                onChange: (string) => {
                  note.value = string.replace(/\n/g, "");
                },
                value
              }
            ),
            " ",
            "\xA0",
            /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
              Button,
              {
                disabled: readOnly,
                style: { padding: 2 },
                onClick: () => {
                  notes.splice(i, 1);
                },
                minimal: true,
                icon: "trash"
              }
            ) })
          ]
        },
        i
      );
    }),
    /* @__PURE__ */ jsxs(
      Button,
      {
        disabled: readOnly,
        onClick: () => {
          notes.push({
            key: "note",
            value: ""
          });
        },
        minimal: true,
        icon: "plus",
        children: [
          " ",
          "Add Note"
        ]
      }
    )
  ] });
});
const StrandField = generateField(({ input }) => {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        title: "Bottom",
        className: "tg-arrowheadType-BOTTOM",
        active: input.value === "BOTTOM",
        minimal: true,
        style: { marginRight: 20 },
        onClick: () => input.onChange("BOTTOM"),
        icon: reverseFeatureIcon
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        title: "No Direction",
        className: "tg-arrowheadType-NONE",
        active: input.value === "NONE",
        minimal: true,
        style: { marginRight: 20 },
        onClick: () => input.onChange("NONE"),
        icon: bluntFeatureIcon
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        title: "Top",
        className: "tg-arrowheadType-TOP",
        onClick: () => input.onChange("TOP"),
        minimal: true,
        active: input.value === "TOP",
        icon: featureIcon
      }
    )
  ] });
});
